import { useRef, useState, type ChangeEvent, type DragEvent } from "react"
import { useTranslation } from "react-i18next"
import { Check, ChevronsUpDown, ImagePlus, Loader2, Search } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import { COMPRESSION_CONFIG } from "../../../config/compressionConfig"
import { compressImageWithLoading } from "utils/imageCompression"
import { formatProductTypeName } from "utils/formatProductTypeName"
import { Errors } from "./Errors"
import { SelectWithInfoTooltip } from "./SelectWithInfoTooltip"

type MerchantOption = { id: string; name: string }
type TypeOption = { id: string; name: string }

interface MakeupMetadataCardProps {
  name: string
  typeId?: string
  region: string
  finish: string
  coverage: string
  cover: string
  merchant: string
  sortedTypes: TypeOption[]
  regions: string[]
  finishes: string[]
  coverages: string[]
  merchantOptions: MerchantOption[]
  showMerchantSelect: boolean
  visible: boolean
  onVisibleChange: (value: boolean) => void
  visibilityDisabled: boolean
  visibilityDisabledHint: string
  autoFocusName?: boolean
  nameErrors?: string[]
  typeErrors?: string[]
  regionErrors?: string[]
  finishErrors?: string[]
  coverageErrors?: string[]
  coverErrors?: string[]
  merchantErrors?: string[]
  onNameChange: (value: string) => void
  onTypeChange: (value: string) => void
  onRegionChange: (value: string) => void
  onFinishChange: (value: string) => void
  onCoverageChange: (value: string) => void
  onCoverChange: (file: File) => void
  onMerchantChange: (value: string) => void
}

const ALLOWED_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"]
const MAX_BYTES = 10 * 1024 * 1024

const validateFile = (file: File) => {
  if (file.size > MAX_BYTES)
    return { ok: false, error: `File size exceeds ${Math.round(MAX_BYTES / 1024 / 1024)}MB` }
  if (!ALLOWED_MIME_TYPES.includes(file.type))
    return { ok: false, error: `Invalid file type. Use PNG, JPG, WebP or GIF.` }
  return { ok: true as const }
}

export const MakeupMetadataCard = ({
  name,
  typeId,
  region,
  finish,
  coverage,
  cover,
  merchant,
  sortedTypes,
  regions,
  finishes,
  coverages,
  merchantOptions,
  showMerchantSelect,
  visible,
  onVisibleChange,
  visibilityDisabled,
  visibilityDisabledHint,
  autoFocusName = false,
  nameErrors,
  typeErrors,
  regionErrors,
  finishErrors,
  coverageErrors,
  coverErrors,
  merchantErrors,
  onNameChange,
  onTypeChange,
  onRegionChange,
  onFinishChange,
  onCoverageChange,
  onCoverChange,
  onMerchantChange,
}: MakeupMetadataCardProps) => {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [merchantPopoverOpen, setMerchantPopoverOpen] = useState(false)
  const [merchantSearch, setMerchantSearch] = useState("")
  const [isCompressing, setIsCompressing] = useState(false)
  const [compressionProgress, setCompressionProgress] = useState(0)
  const [localError, setLocalError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const processFile = async (file: File) => {
    setLocalError(null)
    const validation = validateFile(file)
    if (!validation.ok) {
      setLocalError(validation.error ?? "Invalid file")
      return
    }

    setIsCompressing(true)
    setCompressionProgress(0)
    try {
      const result = await compressImageWithLoading(
        file,
        (progress) => setCompressionProgress(progress),
        COMPRESSION_CONFIG
      )
      onCoverChange(result.compressedFile)
    } catch {
      setLocalError("Failed to process image. Please try again.")
      onCoverChange(file)
    } finally {
      setIsCompressing(false)
      setCompressionProgress(0)
    }
  }

  const handleFileInput = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) await processFile(file)
    event.target.value = ""
  }

  const handleDrop = async (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setIsDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) await processFile(file)
  }

  const filteredMerchants = merchantOptions.filter(
    (m) =>
      !merchantSearch ||
      m.name.toLowerCase().includes(merchantSearch.toLowerCase()) ||
      m.id.toLowerCase().includes(merchantSearch.toLowerCase())
  )

  const selectedMerchantLabel = merchant
    ? merchantOptions.find((m) => m.id === merchant)?.name || merchant
    : "Select merchant..."

  const coverErrorStrings = [...(coverErrors ?? []), ...(localError ? [localError] : [])]

  return (
    <Card data-testid="makeup-metadata-card">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>{t("productPage.productCardInfo")}</CardTitle>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-sm text-muted-foreground">
                  {t("makeup.table.tryOnButton")}
                </span>
                <Switch
                  data-testid="makeup-product-visibility"
                  checked={visible}
                  disabled={visibilityDisabled}
                  onCheckedChange={onVisibleChange}
                />
              </div>
            </TooltipTrigger>
            {visibilityDisabled ? <TooltipContent>{visibilityDisabledHint}</TooltipContent> : null}
          </Tooltip>
        </TooltipProvider>
      </CardHeader>
      <CardContent className="grid items-start gap-6 lg:grid-cols-[1fr_220px]">
        <FieldGroup>
          <Field data-invalid={nameErrors && nameErrors.length > 0 ? true : undefined}>
            <FieldLabel htmlFor="makeup-name">{t("productPage.productNameLabel")}</FieldLabel>
            <Input
              data-testid="makeup-product-name-input"
              id="makeup-name"
              autoFocus={autoFocusName}
              placeholder={t("productPage.productNamePlaceholder")}
              value={name}
              aria-invalid={nameErrors && nameErrors.length > 0 ? true : undefined}
              onChange={(event) => onNameChange(event.target.value)}
            />
            {nameErrors && nameErrors.length > 0 ? <FieldError>{nameErrors[0]}</FieldError> : null}
          </Field>

          <SelectWithInfoTooltip
            testId="makeup-product-type-select"
            name="product_type"
            label={t("productPage.productTypeLabel")}
            onChange={onTypeChange}
            value={typeId ?? ""}
            errors={typeErrors}
            placeholder={t("productPage.productTypePlaceholder")}
          >
            {sortedTypes.map((typeOption) => (
              <option key={typeOption.id} value={typeOption.id}>
                {formatProductTypeName(typeOption.name)}
              </option>
            ))}
          </SelectWithInfoTooltip>

          {showMerchantSelect ? (
            <Field data-invalid={merchantErrors && merchantErrors.length > 0 ? true : undefined}>
              <FieldLabel>{t("productPage.merchantIdLabel")}</FieldLabel>
              <Popover open={merchantPopoverOpen} onOpenChange={setMerchantPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="makeup-merchant-select"
                    className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm hover:bg-accent"
                  >
                    <span className="truncate text-left">{selectedMerchantLabel}</span>
                    <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                >
                  <div className="flex items-center border-b px-3">
                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                    <input
                      data-testid="makeup-merchant-search"
                      className="flex h-9 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
                      placeholder="Search merchants..."
                      value={merchantSearch}
                      onChange={(event) => setMerchantSearch(event.target.value)}
                    />
                  </div>
                  <div className="max-h-[200px] overflow-y-auto p-1">
                    {filteredMerchants.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        data-testid="makeup-merchant-option"
                        data-merchant-id={m.id}
                        className="flex w-full cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                        onClick={() => {
                          onMerchantChange(m.id)
                          setMerchantPopoverOpen(false)
                          setMerchantSearch("")
                        }}
                      >
                        <Check
                          className={cn(
                            "h-4 w-4 shrink-0",
                            merchant === m.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="truncate">
                          {m.name} - {m.id}
                        </span>
                      </button>
                    ))}
                    {filteredMerchants.length === 0 ? (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        No merchants found.
                      </p>
                    ) : null}
                  </div>
                </PopoverContent>
              </Popover>
              {merchantErrors && merchantErrors.length > 0 ? (
                <FieldError>{merchantErrors[0]}</FieldError>
              ) : null}
            </Field>
          ) : null}
        </FieldGroup>

        <div className="space-y-1.5">
          <Label className="sr-only">{t("productPage.uploadImage")}</Label>
          <input
            ref={fileInputRef}
            accept={ALLOWED_MIME_TYPES.join(",")}
            className="hidden"
            type="file"
            data-testid="makeup-cover-file-input"
            onChange={handleFileInput}
          />
          <button
            type="button"
            data-testid="makeup-cover-upload"
            disabled={isCompressing}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(event) => {
              event.preventDefault()
              setIsDragging(true)
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={cn(
              "group flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl border border-dashed border-border bg-muted transition",
              "hover:border-vto-accent/40 hover:bg-muted/80",
              isDragging && "border-vto-accent/60 bg-muted",
              coverErrorStrings.length > 0 && !cover && "border-destructive",
              isCompressing && "cursor-not-allowed opacity-70"
            )}
          >
            {isCompressing ? (
              <div className="flex flex-col items-center gap-2 px-4 text-center">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <p className="text-xs text-muted-foreground">{compressionProgress}%</p>
              </div>
            ) : cover ? (
              <img src={cover} alt={name || "Product"} className="h-full w-full object-cover" />
            ) : (
              <div className="flex flex-col items-center gap-2 px-4 text-center">
                <ImagePlus className="h-5 w-5 text-muted-foreground" />
                <p className="text-sm font-medium">{t("productPage.uploadImage")}</p>
                <p className="text-xs leading-snug text-muted-foreground">
                  PNG, JPG, WebP
                  <br />
                  up to 10MB
                </p>
              </div>
            )}
          </button>
          {coverErrorStrings.length > 0 ? <Errors _errors={coverErrorStrings} /> : null}
        </div>

        <div className="border-t pt-5 lg:col-span-2">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <SelectWithInfoTooltip
              testId="makeup-region-select"
              label={t("productPage.regionLabel")}
              name="region"
              onChange={onRegionChange}
              disabled={!typeId?.length}
              errors={regionErrors}
              placeholder={t("productPage.regionPlaceholder")}
              value={region}
            >
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </SelectWithInfoTooltip>

            <SelectWithInfoTooltip
              testId="makeup-finish-select"
              label={t("productPage.finishLabel")}
              name="finish"
              onChange={onFinishChange}
              disabled={!region?.length}
              value={finish}
              errors={finishErrors}
              placeholder={t("productPage.finishPlaceholder")}
            >
              {finishes.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </SelectWithInfoTooltip>

            <SelectWithInfoTooltip
              testId="makeup-coverage-select"
              disabled={!finish?.length}
              errors={coverageErrors}
              label={t("productPage.coverageLabel")}
              name="coverage"
              onChange={onCoverageChange}
              placeholder={t("productPage.coveragePlaceholder")}
              value={coverage}
            >
              {coverages.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </SelectWithInfoTooltip>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
